import * as tf from '@tensorflow/tfjs';

// Simple tokenizer for demo purposes
class SimpleTokenizer {
    constructor() {
        this.vocab = new Map();
        this.padTokenId = 1;
        this.maxLength = 512;
    }
    
    async load() {
        try {
            const response = await fetch('/tfjs-compatible-model/vocab.txt');
            const vocabText = await response.text();
            const words = vocabText.split('\n');
            words.forEach((word, index) => {
                if (word.trim()) {
                    this.vocab.set(word.trim(), index);
                }
            });
        } catch (error) {
            console.warn('Could not load vocab.txt, using fallback tokenization');
        }
    }
    
    tokenize(text) {
        // Simple word-based tokenization
        const words = text.toLowerCase().split(/\s+/);
        const tokens = [];
        
        for (const word of words) {
            if (this.vocab.has(word)) {
                tokens.push(this.vocab.get(word));
            } else {
                // Use unknown token or random ID for unknown words
                tokens.push(Math.floor(Math.random() * 1000) + 100);
            }
        }
        
        // Pad or truncate to maxLength
        while (tokens.length < this.maxLength) {
            tokens.push(this.padTokenId);
        }
        
        return tokens.slice(0, this.maxLength);
    }
}

// Simple classifier for demo purposes
class SimpleClassifier {
    constructor(labelSet) {
        this.labelSet = labelSet;
    }
    
    predict(embeddings, text) {
        // Simple rule-based classification for demo
        // In production, you'd use the actual trained classifier
        const predictions = new Array(this.labelSet.length).fill(0);
        
        // Simple keyword matching
        const lowerText = text.toLowerCase();
        
        if (lowerText.includes('halal')) predictions[1] = 1;
        if (lowerText.includes('vegan')) predictions[4] = 1;
        if (lowerText.includes('vegetarian')) predictions[5] = 1;
        if (lowerText.includes('kosher')) predictions[2] = 1;
        if (lowerText.includes('gluten') || lowerText.includes('gluten-free')) predictions[0] = 1;
        if (lowerText.includes('pescatarian') || lowerText.includes('fish') || lowerText.includes('seafood')) predictions[3] = 1;
        
        return predictions;
    }
}

export class TextClassificationHelper {
    constructor() {
        this.model = null;
        this.tokenizer = null;
        this.labelSet = ['gluten-free', 'halal', 'kosher', 'pescatarian', 'vegan', 'vegetarian'];
        this.isModelLoaded = false;
    }

    async loadModel() {
        try {
            console.log('Loading TensorFlow.js model...');
            
            // Load the TensorFlow.js model
            this.model = await tf.loadGraphModel('/tfjs-setfit-model/model.json');
            
            // Initialize tokenizer
            this.tokenizer = new SimpleTokenizer();
            await this.tokenizer.load();
            
            this.isModelLoaded = true;
            console.log('Model loaded successfully!');
            
            return { success: true };
        } catch (error) {
            console.error('Error loading model:', error);
            this.isModelLoaded = false;
            return { success: false, error: error.message };
        }
    }

    async classifyText(text) {
        if (!text || !text.trim()) {
            return { success: false, error: 'Please provide text to classify.' };
        }

        if (!this.isModelLoaded || !this.model || !this.tokenizer) {
            return { success: false, error: 'Model not loaded yet. Please load the model first.' };
        }

        try {
            console.log('Processing text for classification...');
            
            // Tokenize the input text
            const tokens = this.tokenizer.tokenize(text);
            
            // Convert to tensor
            const inputTensor = tf.tensor2d([tokens], [1, 512]);
            
            // Get embeddings from model
            const embeddings = await this.model.predict(inputTensor);
            
            // Use simple classifier for demo
            const classifier = new SimpleClassifier(this.labelSet);
            const predictions = classifier.predict(embeddings, text);
            
            // Clean up tensors
            inputTensor.dispose();
            embeddings.dispose();
            
            // Format results
            const results = this.formatResults(predictions, text);
            
            return { success: true, results };
            
        } catch (error) {
            console.error('Error during classification:', error);
            return { success: false, error: error.message };
        }
    }

    formatResults(predictions, text) {
        const results = [];
        
        predictions.forEach((prediction, index) => {
            if (prediction > 0) {
                results.push({
                    label: this.labelSet[index],
                    confidence: prediction,
                    text: text
                });
            }
        });
        
        return {
            predictions: results,
            detectedLabels: results.map(r => r.label),
            originalText: text,
            labelSet: this.labelSet
        };
    }

    getLabelSet() {
        return this.labelSet;
    }

    isModelReady() {
        return this.isModelLoaded;
    }

    // Utility method to check if text contains any dietary keywords
    hasDietaryKeywords(text) {
        const lowerText = text.toLowerCase();
        const keywords = [
            'halal', 'vegan', 'vegetarian', 'kosher', 
            'gluten', 'gluten-free', 'pescatarian', 
            'fish', 'seafood', 'dietary', 'restriction'
        ];
        
        return keywords.some(keyword => lowerText.includes(keyword));
    }
}

// Export a singleton instance
export const textClassificationHelper = new TextClassificationHelper(); 